import React, {useState, useEffect, memo} from 'react';
import {navigate} from 'gatsby';
import styled from 'styled-components';
import Collapse from '@material-ui/core/Collapse';
import {Subhead, Caption2, Body} from '../../../components/Typography';
import {PrimaryButton, ButtonRow} from '../../../components/Buttons';
import renderCurrentOpenStatus from '../utils/renderCurrentOpenStatus';
import ChildLocations from '../../ParentChild/LocationSelector/ChildLocations';
import appConfig from '../../../../app-config';
import {getParentInformation} from '../../OrderSetting/orderSettingActions';

const Wrapper = styled.div`
  display: grid;
  grid-gap: 32px;
  margin-top: 24px;
  align-items: center;

  @media (max-width: 786px) {
    margin-top: 0;
    padding: 20px;
    border-radius: 16px;
    box-shadow: {props.isOrderButtonHidden ? 0 -1px 8px 0 rgba(0, 0, 0, 0.15) : 0};
    z-index: 999;
    overflow: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const MobileDetails = styled.div`
  display: grid;
  grid-template-columns: 36px 1fr auto;
  align-items: center;
  grid-gap: 16px;
`;

const MobileBackChevron = styled.i`
  @media (min-width: 786px) {
    display: none;
  }
`;

const MobileLocationName = styled(Subhead)`
  grid-column: 2 / -1;
`;

const MobileAddress = styled.div`
  grid-column: 2;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr auto;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled(Body)`
  color: #737a87;
  font-weight: 600;
  align-self: flex-start;
`;

const HoursWrapper = styled(Collapse)`
  grid-column: 2 / -1;
`;

const HoursRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const DayOfWeek = styled(Caption2)`
  text-transform: capitalize;
`;

const DesktopButtonRow = styled(ButtonRow)`
  @media (max-width: 786px) {
    display: none;
  }
`;

const MobileButtonRow = styled(ButtonRow)`
  @media (min-width: 786px) {
    display: none;
  }
`;

const HoursTypeTitle = styled(Subhead)`
  margin-bottom: 8px;
  margin-top: 15px;
`;

const LocationDetails = ({
  location,
  setLocationToShowOnMobile,
  orderFromHere,
  isOrderFromHereShowing,
  currentLocation,
  updateOrderSettingModalIsVisible,
  updateInterOrderType,
  isOrderButtonHidden,
  isChevronHidden,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [childLocations, setChildLocations] = useState(null);

  useEffect(() => {
    if (appConfig.isParentChildClient) {
      getParentInformation(location.id).then(business => {
        setChildLocations(business.sub_businesses);
        return;
      });
    }
  }, [location.id]);

  const [
    idOfExpandedChildLocationPreview,
    setIdOfExpandedChildLocationPreview,
  ] = useState(null);

  const handleSetIdOfExpandedChildLocationPreview = idOfExpandedChildLocationPreviewToSet => {
    if (
      idOfExpandedChildLocationPreviewToSet === idOfExpandedChildLocationPreview
    ) {
      setIdOfExpandedChildLocationPreview(null);
    } else {
      setIdOfExpandedChildLocationPreview(
        idOfExpandedChildLocationPreviewToSet
      );
    }
  };

  const handleHoursClick = event => {
    event.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleOrderFromHere = (event, location) => {
    event.stopPropagation();
    if (
      location.accepted_order_types.length === 1 &&
      location.accepted_order_types[0] === 'delivery'
    ) {
      navigate('/menu');
      updateInterOrderType('delivery');
      updateOrderSettingModalIsVisible(true);
    } else {
      orderFromHere(location);
    }
  };

  return (
    <Wrapper isOrderButtonHidden={isOrderButtonHidden}>
      {typeof window !== 'undefined' && window.innerWidth <= 768 && (
        <MobileDetails>
          {!isChevronHidden ? (
            <MobileBackChevron
              className="fal fa-chevron-left"
              onClick={() => setLocationToShowOnMobile(null)}
            />
          ) : null}
          <MobileLocationName id="LocationDetails-MobileLocationName">
            {location.name}
          </MobileLocationName>
          <MobileAddress>
            <Caption2>{location.line1}</Caption2>
            <Caption2>{renderCurrentOpenStatus(location)}</Caption2>
          </MobileAddress>
          {currentLocation && <Caption2>{location.distance}</Caption2>}
        </MobileDetails>
      )}
      <MobileButtonRow justifyContent="center">
        {!isOrderButtonHidden ? (
          <PrimaryButton
            id="LocationDetails-MobileOrderButton"
            onClick={e => handleOrderFromHere(e, location)}
          >
            {location.accepting_orders && location.is_order_ahead_enabled
              ? 'Order From Here'
              : 'See Menu'}
          </PrimaryButton>
        ) : null}
      </MobileButtonRow>

      <Row onClick={handleHoursClick}>
        <Label id="LocationDetails-Hours">Hours</Label>
        <Caption2>{renderCurrentOpenStatus(location)}</Caption2>
        <i className={`fal fa-chevron-${isExpanded ? 'down' : 'right'}`} />
        <HoursWrapper in={isExpanded}>
          <HoursTypeTitle id="LocationDetails-StoreHours">
            Store Hours
          </HoursTypeTitle>
          {Object.keys(location.hours).map(dayOfWeek => {
            return (
              <HoursRow key={dayOfWeek}>
                <DayOfWeek>{dayOfWeek}</DayOfWeek>
                <Caption2>
                  {location.hours[dayOfWeek][0] &&
                  location.hours[dayOfWeek][0].length
                    ? location.hours[dayOfWeek][0]
                    : 'Closed'}
                </Caption2>
              </HoursRow>
            );
          })}
          <HoursTypeTitle id="LocationDetails-OrderingHours">
            Ordering Hours
          </HoursTypeTitle>
          {Object.keys(location.ordering_hours).map(dayOfWeek => {
            return (
              <HoursRow key={dayOfWeek}>
                <DayOfWeek>{dayOfWeek}</DayOfWeek>
                <Caption2>
                  {location.ordering_hours[dayOfWeek][0] &&
                  location.ordering_hours[dayOfWeek][0].length
                    ? location.ordering_hours[dayOfWeek][0]
                    : 'Closed'}
                </Caption2>
              </HoursRow>
            );
          })}
        </HoursWrapper>
      </Row>
      <Row>
        <Label id="LocationDetails-PhoneLabel">Phone</Label>
        <Caption2>{location.phone}</Caption2>
        <i className="fas fa-phone" />
      </Row>
      <Row>
        <Label id="LocationDetails-AddressLabel">Address</Label>
        <div>
          <Caption2 id="LocationDetails-Location">{location.line1}</Caption2>
          <Caption2>
            {location.city}, {location.state}
          </Caption2>
          <Caption2 id="LocationDetails-Zip">{location.zip}</Caption2>
        </div>
        <i className="fas fa-map-marker-alt" />
      </Row>

      {appConfig.isParentChildClient && (
        <Row>
          <Label id="LocationDetails-AddressLabel">Stores</Label>
          <div>
            {childLocations
              ? childLocations.map(subBusiness => {
                  return (
                    <ChildLocations
                      key={subBusiness.id}
                      subBusiness={subBusiness}
                      idOfExpandedChildLocationPreview={
                        idOfExpandedChildLocationPreview
                      }
                      setIdOfExpandedChildLocationPreview={
                        handleSetIdOfExpandedChildLocationPreview
                      }
                    />
                  );
                })
              : null}
          </div>
        </Row>
      )}
      {!appConfig.isParentChildClient && !isOrderButtonHidden && (
        <DesktopButtonRow justifyContent="center">
          {isOrderFromHereShowing && location.is_order_ahead_enabled && (
            <PrimaryButton
              id="LocationDetails-DesktopOrderButton"
              onClick={e => handleOrderFromHere(e, location)}
            >
              Order From Here
            </PrimaryButton>
          )}
        </DesktopButtonRow>
      )}
    </Wrapper>
  );
};

export default memo(LocationDetails);
